import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Box, FormControlLabel, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { useStyles } from '../styles';
import { AddAgentPromptFormValue } from '../types';
import { MarkdownEditor } from '@shared/components/MarkdownEditor';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { TurnHandlingFields } from './TurnHandlingFields';
import { SpeechPacingFields } from './SpeechPacingFields';

interface AddAgentProfileFormProps {
  template?: AddAgentPromptFormValue;
}

export const AddAgentProfileForm: React.FC<AddAgentProfileFormProps> = ({ template }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext<AddAgentPromptFormValue>();

  useEffect(() => {
    if (!template) return;

    setValue('name', template.name);
    setValue('prompt', template.prompt);
    setValue('firstMessage', template.firstMessage ?? '');
    setValue(
      'firstMessageInterruptionsEnabled',
      template.firstMessageInterruptionsEnabled ?? false,
    );
    setValue('turnHandling', template.turnHandling ?? undefined);
    setValue('speechPacing', template.speechPacing ?? undefined);
  }, [setValue, template]);

  return (
    <>
      <Box className={classes.formField}>
        <Controller
          name="name"
          control={control}
          rules={{ required: t('assistantsPage.modals.validation.name') }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('assistantsPage.modals.add.nameLabel')}
              error={!!errors.name}
              helperText={errors.name?.message}
              variant="outlined"
              className={classes.filledField}
            />
          )}
        />
      </Box>
      <Box className={classes.formField}>
        <Controller
          name="prompt"
          control={control}
          rules={{ required: t('assistantsPage.modals.validation.prompt') }}
          render={({ field }) => {
            return (
              <Box>
                <MarkdownEditor
                  value={field.value}
                  onChange={field.onChange}
                  label={t('assistantsPage.modals.add.promptLabel')}
                  error={!!errors.prompt}
                  helperText={errors.prompt?.message}
                  required
                  height={500}
                />
              </Box>
            );
          }}
        />
      </Box>
      <Box className={classes.formField}>
        <Controller
          name="firstMessage"
          control={control}
          render={({ field }) => (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {t('assistantsPage.modals.openingLine.label')}
              </Typography>
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={3}
                placeholder={t('assistantsPage.modals.openingLine.placeholder')}
                variant="outlined"
                className={classes.filledField}
              />
            </>
          )}
        />
      </Box>
      <Box className={classes.formField}>
        <Controller
          name="firstMessageInterruptionsEnabled"
          control={control}
          render={({ field }) => (
            <>
              <FormControlLabel
                control={
                  <CustomSwitch
                    sx={{ ml: '20px', mr: '14px' }}
                    checked={field.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      field.onChange(e.target.checked)
                    }
                  />
                }
                label={t('assistantsPage.modals.firstMessageInterruptionsEnabled.label')}
              />
            </>
          )}
        />
      </Box>
      <Box className={classes.formField}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <TurnHandlingFields control={control as any} />
      </Box>
      <Box className={classes.formField}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <SpeechPacingFields control={control as any} />
      </Box>
    </>
  );
};
